"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { useTranslation, I18nextProvider } from "react-i18next";
import i18n from "@/lib/i18n";
import {
  emitAppResumeRequest,
  emitAppSuspend,
  type AppResumeReason,
} from "@/lib/appLifecycle";

type Language = "ko" | "en" | "mn" | "zh-CN" | "zh-TW" | "vi";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  languageLabel: string;
}

export const LANGUAGE_OPTIONS: { value: Language; label: string }[] = [
  { value: "ko", label: "한국어" },
  { value: "en", label: "English" },
  { value: "mn", label: "Монгол хэл" },
  { value: "zh-CN", label: "简体中文" },
  { value: "zh-TW", label: "繁體中文" },
  { value: "vi", label: "Tiếng Việt" },
];

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("ko");

  useEffect(() => {
    const saved = localStorage.getItem("seexr-language") as Language;
    const nextLanguage = saved && LANGUAGE_OPTIONS.some(l => l.value === saved) ? saved : "ko";
    document.documentElement.lang = nextLanguage;
    void i18n.changeLanguage(nextLanguage).then(() => setLanguageState(nextLanguage));
  }, []);

  useEffect(() => {
    let lastResumeAt = 0;

    const dispatchResume = (reason: AppResumeReason, persisted?: boolean) => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;

      const now = Date.now();
      // Focusing a background tab normally fires both visibilitychange and focus.
      if (now - lastResumeAt < 750) return;
      lastResumeAt = now;
      // Browser lifecycle 신호는 먼저 session gate에 전달한다. 실제
      // APP_RESUME_EVENT는 인증 상태가 정리된 뒤 ProtectedSessionGate가 발행한다.
      emitAppResumeRequest({ at: now, reason, persisted });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") dispatchResume("visibilitychange");
    };
    const handleFocus = () => dispatchResume("focus");
    const handleOnline = () => dispatchResume("online");
    const handlePopState = () => dispatchResume("popstate");
    const handlePageShow = (event: PageTransitionEvent) =>
      dispatchResume("pageshow", event.persisted);
    const handlePageHide = (event: PageTransitionEvent) =>
      emitAppSuspend({
        at: Date.now(),
        reason: "pagehide",
        persisted: event.persisted,
      });
    const handleFreeze = () =>
      emitAppSuspend({ at: Date.now(), reason: "freeze" });
    const handleDocumentResume = () => dispatchResume("resume");

    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("freeze", handleFreeze);
    document.addEventListener("resume", handleDocumentResume);
    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("freeze", handleFreeze);
      document.removeEventListener("resume", handleDocumentResume);
    };
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("seexr-language", lang);
    document.documentElement.lang = lang;
    i18n.changeLanguage(lang);
  };

  const languageLabel = LANGUAGE_OPTIONS.find(l => l.value === language)?.label || "한국어";

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={{ language, setLanguage, languageLabel }}>
        {children}
      </LanguageContext.Provider>
    </I18nextProvider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  const { t } = useTranslation();
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return { ...context, t };
}
